import { Box, Breadcrumbs, Button, Divider, Group, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { Link, useSearchParams } from 'react-router';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { BreadcrumbTrail } from 'src/UI/app-container/BreadcrumbTrail';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { householdName } from 'src/financial/financial-data/financial-item/rework/householdName';
import {
  financialsReturnPath,
  RETURN_TO_PARAM,
} from 'src/financial/financial-data/financial-item/rework/returnPath';
import PageLoader from 'src/UI/loader/PageLoader';
import {
  UpsertEntityInput,
  useUpsertEntity,
} from 'src/data-capture/merge-review/useUpsertEntities';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import useFinancialEntityLayout from 'src/UI/dynamicUI/hooks/useFinancialEntityLayout';
import DynamicFormNew from 'src/UI/dynamicUI/layout/DynamicFormNew';
import { EntityFields, EntityMetadata, EntityWithFieldMetadata } from 'src/UI/dynamicUI/types';
import { ClientListItem } from 'src/admin/users/types';
import ModalBackButton from 'src/UI/common/NavBackButton';
import { DefinedBenefitPension } from 'src/financial/financial-data/financial-item/forms/DefinedBenefitPension';
import { ProductFormComponent } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { PensionAnnuity } from 'src/financial/financial-data/financial-item/forms/PensionAnnuity';
import { Property } from 'src/financial/financial-data/financial-item/forms/Property';
import { SpendingAccount } from 'src/financial/financial-data/financial-item/forms/SpendingAccount';
import { Mortgage } from 'src/financial/financial-data/financial-item/forms/Mortgage';
import { Protection } from 'src/financial/financial-data/financial-item/forms/Protection';
import { Crypto } from 'src/financial/financial-data/financial-item/forms/Crypto';
import { Loan } from 'src/financial/financial-data/financial-item/forms/Loan';
import { InvestmentAndCash } from 'src/financial/financial-data/financial-item/forms/InvestmentAndCash';
import { Collectible } from 'src/financial/financial-data/financial-item/forms/Collectible';
import { IncomeDrawdown } from 'src/financial/financial-data/financial-item/forms/IncomeDrawdown';
import { DefinedContributionPension } from 'src/financial/financial-data/financial-item/forms/DefinedContributionPension';
import { StatePension } from 'src/financial/financial-data/financial-item/forms/StatePension';

type CommonProps = {
  product: string;
  client: ClientListItem;
  onSuccessfulSave: (id: string) => void;
  backHref?: string;
};

type CreateProps = CommonProps;

type UpdateProps = CommonProps & {
  isCreateMode: false;
  onDeleteConfirm?: () => void;
  /** Connecting to a feed rewrites the item in place; saving it may navigate away. */
  onAssetConnected?: (id: string) => void;
  entityWithMetadata: EntityWithFieldMetadata;
};

export const FinancialItemDataDynamicForm = ({
  product,
  client,
  onSuccessfulSave,
  backHref,
  ...props
}: CreateProps | UpdateProps) => {
  const { layout, isLoading: isLoadingLayout } = useFinancialEntityLayout({ entityType: product });
  const { flagEnabledFinancialRework } = useFlags();
  const { members } = useFamilyMembers({ enabled: flagEnabledFinancialRework });
  const [searchParams] = useSearchParams();

  const { upsertEntity } = useUpsertEntity();

  const isCreateMode = !('entityWithMetadata' in props);

  const handleAddPress = async (clientId: string, upsertEntities: UpsertEntityInput[]) => {
    try {
      if (!upsertEntities || upsertEntities.length === 0) {
        throw Error('No financial entities to create');
      }

      const upsertedEntity = await upsertEntity(clientId, upsertEntities[0], true);
      if (onSuccessfulSave && upsertedEntity && upsertedEntity) {
        onSuccessfulSave(upsertedEntity);
      }
    } catch (e) {
      notifyAndLogError(
        'Error saving item',
        'Something when wrong trying to save the item. Please try again and contact support if the issue persists.',
        e,
        { product },
      );
    }
  };

  const onSave = async (mappedValues: EntityFields | EntityFields[], metadata: EntityMetadata) => {
    try {
      const entityToUpsert: UpsertEntityInput = {
        entityFieldsMetadata: metadata,
        entityType: product,
        values: mappedValues,
      };
      const upsertedEntity = await upsertEntity(client.id, entityToUpsert, true);
      if (onSuccessfulSave && upsertedEntity) {
        onSuccessfulSave(upsertedEntity);
      }
    } catch (e) {
      notifyAndLogError(
        'Error saving item',
        'Something when wrong trying to save the item. Please try again and contact support if the issue persists.',
        e,
        { product },
      );
    }
  };

  function getProductComponent(product: string): ProductFormComponent | null {
    switch (product) {
      case 'DefinedBenefitPension':
        return DefinedBenefitPension;
      case 'PensionAnnuity':
        return PensionAnnuity;
      case 'Property':
        return Property;
      case 'SpendingAccount':
        return SpendingAccount;
      case 'Mortgage':
        return Mortgage;
      case 'Protection':
        return Protection;
      case 'Crypto':
        return Crypto;
      case 'Collectible':
        return Collectible;
      case 'Loan':
        return Loan;
      case 'InvestmentAndCash':
        return InvestmentAndCash;
      case 'IncomeDrawdownPension':
        return IncomeDrawdown;
      case 'DefinedContributionPension':
        return DefinedContributionPension;
      case 'StatePension':
        return StatePension;
      default:
        return null;
    }
  }

  const renderContent = () => {
    if (isLoadingLayout) {
      return <PageLoader />;
    }

    if (!product || !layout) {
      return null;
    }

    const ProductFormComponent = getProductComponent(product);
    if (ProductFormComponent) {
      return isCreateMode ? (
        <ProductFormComponent layout={layout} onSave={onSave} />
      ) : (
        <ProductFormComponent
          layout={layout}
          entityWithMetadata={props.entityWithMetadata}
          onDeleteConfirm={props.onDeleteConfirm}
          onSave={onSave}
          onConnectAsset={props.onAssetConnected ?? onSuccessfulSave}
        />
      );
    }

    const metadata: EntityMetadata = layout?.entitiesMetadata?.[product] || {};
    const fields: EntityFields = Object.keys(metadata).reduce(
      (acc, key) => ({
        ...acc,
        [key]: undefined,
      }),
      {} as EntityFields,
    );

    const entity: EntityWithFieldMetadata = isCreateMode
      ? {
          fields,
          metadata: metadata,
          entityType: product,
        }
      : props.entityWithMetadata;

    const uiData = {
      entities: {
        [product]: {
          id: '',
          entityType: product,
          entityLabel: product,
          entity: isCreateMode ? entity : props.entityWithMetadata,
        },
      },
      ui: { ...layout },
    };

    return (
      <>
        <Divider my={'xl'} />
        <DynamicFormNew
          uiData={uiData}
          onSubmit={handleAddPress}
          isCreateMode={isCreateMode}
          isDeletable={!isCreateMode}
          onDelete={() => {}}
          renderUiEntityTitle={() => (
            <Group>
              <ModalBackButton />
              <Text size="lg" fw={700}>
                This is a placeholder and is still in development
              </Text>
            </Group>
          )}
        />
      </>
    );
  };

  const clientName = [client?.firstName, client?.lastName].filter(Boolean).join(' ');
  // An item owned across a household reads under the household's name, not whichever member
  // the record happens to hang off — so the crumb goes to the family too, where there is one.
  const ownerName = householdName(members, clientName);
  const ownerHref = client.familyId
    ? `/family/${client.familyId}/profile`
    : `/client/${client.id}/non-financial-data`;
  // Wherever the item was opened from is where its Financials crumb points, the same place
  // saving and deleting return to.
  const financialsHref = financialsReturnPath(client.id, searchParams.get(RETURN_TO_PARAM));

  return (
    <Box px={'md'}>
      {flagEnabledFinancialRework ? (
        <BreadcrumbTrail
          mb="sm"
          mt="xs"
          size="xs"
          color="gray.5"
          compact
          endsAtCurrentPage={false}
          items={[
            { label: 'All clients', href: '/clients/all' },
            { label: ownerName, href: ownerHref },
            { label: 'Financials', href: financialsHref },
          ]}
        />
      ) : (
        <Breadcrumbs separator={<IconChevronRight size={16} />} mb="md" mt="md">
          <Button component={Link} variant="text" to="/clients/all" c={'gray.9'} fw={'500'}>
            <Text size="md">All clients</Text>
          </Button>
          <Button
            component={Link}
            variant="text"
            to={backHref ?? `/client/${client.id}/financial-data`}
            c={'brand.6'}
            fw={'500'}
          >
            <Text size="md">{clientName}</Text>
          </Button>
          <Text c={'gray.10'} fw={'700'} size="md">
            Financials
          </Text>
        </Breadcrumbs>
      )}

      {renderContent()}
    </Box>
  );
};
